import {
  Button,
  CircularProgress,
  Dialog,
  Icon,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import OutlinedButtons from 'app/componentV4/Button/OutlinedButtons'
import OTPInput from 'app/componentV4/Input/OTPInput'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import LabelInput from 'app/componentV4/Labels/LabelInput'
import MessagespringLoading from 'app/componentV4/Loader/MessagespringLoading'
import { CLOSE, SECONDARY } from 'app/constants/button'
import clsx from 'clsx'
import useOtp from 'commons/useOtp'
import { Form, Formik } from 'formik'
import { equals, isNil } from 'ramda'
import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import * as Yup from 'yup'

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiDialog-paper': {
      overflowX: 'auto',
    },
  },
  input: {
    '&.MuiOutlinedInput-root': {
      height: '48px !important',
    },
  },
  button: {
    '& .button-close': {
      textTransform: 'none',
      padding: '0.75rem 1.25rem',
      color: theme.colors['grey-200'],
      border: '1px solid',
      borderColor: theme.colors['grey-200'],
      borderRadius: '4px',
      backgroundColor: 'transparent !important',
      '&:hover': {
        color: theme.colors['grey-400'],
        borderColor: theme.colors['grey-400'],
      },
      '&:action': {
        color: theme.colors['grey-300'],
        borderColor: theme.colors['grey-300'],
      },
    },
    '& .button-submit': {
      backgroundColor: theme.colors['primary'],
      color: theme.palette.light.main,
      textTransform: 'none',
      padding: '0.75rem 1.25rem',
      '&:hover': {
        backgroundColor: theme.colors['primary-500'],
      },
      '&:action': {
        backgroundColor: theme.colors['primary'],
      },
    },
    '& .MuiButton-contained.Mui-disabled': {
      backgroundColor: `${theme.colors['grey-100']} !important`,
    },
  },
}))

export default (props) => {
  const {
    open,
    onClose,
    onSubmit,
    isLoading,
    name,
    email,
    refCode,
    isOtpLoading,
    reSendOtp,
    error = undefined,
  } = props
  const classes = useStyles()

  const [seconds, setSeconds] = useState(60)
  const [active, setActive] = useState(true)

  useEffect(() => {
    if (open) {
      if (!active) return

      if (seconds === 0) {
        setActive(false)
        return
      }
      const timer = setTimeout(() => {
        setSeconds((prev) => prev - 1)
      }, 1000)

      return () => clearTimeout(timer)
    } else {
      setSeconds(60)
    }
  }, [seconds, active, open])

  const handleResend = () => {
    reSendOtp()
    setSeconds(60)
    setActive(true)
  }

  return (
    <Dialog open={open} className={classes.root}>
      {isOtpLoading ? (
        <div className="w-512 h-512 flex justify-center items-center">
          <MessagespringLoading />
        </div>
      ) : (
        <div className="mt-3 text-primary">
          <div className="flex justify-between">
            <div className="text-blue-950 text-lg font-bold leading-6 p-5 px-10">
              <FormattedMessage id="transfer.dialog.title" />
            </div>
            <div className="px-5">
              <IconButton
                aria-owns="close button"
                aria-haspopup="true"
                className="p-4"
                disabled={isLoading}
                onClick={onClose}
              >
                <Icon color="action" className="text-24 text-blue-950 font-600">
                  close
                </Icon>
              </IconButton>
            </div>
          </div>

          <div className="my-5 space-y-3 text-left px-10 pb-5">
            <Typography className=" text-14 text-primary">
              <FormattedMessage id="transfer.dialog.content.start" />
              <label className="font-bold px-2">{name}</label>
            </Typography>
            <ul className="text-left list-disc list-inside space-y-3 text-14 text-primary pl-5 ">
              <li>
                <FormattedMessage id="transfer.dialog.list1" />
              </li>
              <li>
                <FormattedMessage id="transfer.dialog.list2" />
              </li>
              <li>
                <FormattedMessage id="transfer.dialog.list3" />
              </li>
            </ul>
          </div>
          <div className="space-y-5 pb-10">
            <Typography className="text-18 font-500 text-primary text-center">
              <FormattedMessage id="teamMembers.otp.verification.label" />
            </Typography>

            <div className="my-10">
              <Typography className="text-14 text-grey-400 text-center">
                <FormattedMessage id="teamMembers.otp.verification.detail.label" />
              </Typography>
              <Typography className="text-14 font-bold text-grey-400 text-center break-all">
                {email}
              </Typography>
            </div>

            <Typography className="flex justify-center space-x-2 text-14 text-grey-400 text-center my-10">
              <span>ref:</span>
              <span>{refCode}</span>
            </Typography>
            <Formik
              initialValues={{ otp: '' }}
              validationSchema={Yup.object({
                otp: Yup.string()
                  .length(6, 'OTP must be 6 digits')
                  .required('Required'),
              })}
              onSubmit={(values, { setSubmitting }) => {
                onSubmit(values.otp)
                setSubmitting(false)
              }}
            >
              {({ dirty, values, setFieldValue, isSubmitting, isValid }) => {
                return (
                  <Form>
                    <OTPInput
                      name="otp"
                      length={6}
                      variant="outlined"
                      value={values.otp}
                      onChange={(val) => {
                        setFieldValue('otp', val)
                      }}
                      inputProps={{
                        'aria-label': 'Verify OTP',
                        error: !isNil(error),
                      }}
                      className="mx-auto space-x-5 md:space-x-10 w-fit"
                    />
                    <div className="flex w-fit mx-auto space-x-1">
                      <Typography className="text-14 text-grey-400 text-center my-10">
                        <FormattedMessage id="teamMembers.cannot.otp.request" />
                      </Typography>
                      {active ? (
                        <Typography className="text-12 text-grey-200 text-center my-10 flex items-center font-500">
                          <FormattedMessage
                            id="teamMembers.resent.text"
                            values={{
                              secondsTime: seconds,
                            }}
                          />
                        </Typography>
                      ) : (
                        <Button
                          className={
                            'p-0 text-secondary text-12 font-400 normal-case disabled:text-secondary hover:bg-transparent text-nowrap'
                          }
                          onClick={handleResend}
                        >
                          <FormattedMessage id="text.resend.now" />
                        </Button>
                      )}
                    </div>
                    <div
                      className={clsx(
                        classes.button,
                        'flex justify-center space-x-5 mt-10',
                      )}
                    >
                      <OutlinedButtons
                        tabIndex={-1}
                        id="close-confirm-password-dialog"
                        type="button"
                        color={CLOSE}
                        disabled={isSubmitting || isLoading}
                        onClick={onClose}
                      >
                        <FormattedMessage id="text.close" />
                      </OutlinedButtons>
                      <ContainedButton
                        id="transfer-account-btn"
                        color={SECONDARY}
                        isLoading={isSubmitting || isLoading}
                        type="submit"
                        disabled={
                          !dirty || isSubmitting || !isValid || isLoading
                        }
                      >
                        <FormattedMessage id="transfer.dialog.btn" />
                      </ContainedButton>
                    </div>
                  </Form>
                )
              }}
            </Formik>
          </div>
        </div>
      )}
    </Dialog>
  )
}
